// Move API connections to another environment (preview → production) as a
// passphrase-sealed file. Import adds only connections the target doesn't
// have yet and merges Billing Sync's PhoneSuite territory list.
import { useMemo, useState } from "react";
import { ArrowRightLeft, Download, Upload, Loader2, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS = {
  new: ["Will be added", "text-emerald-700 bg-emerald-50"],
  exists: ["Already live — kept", "text-slate-500 bg-slate-100"],
  no_connection: ["Skipped — no connection", "text-amber-700 bg-amber-50"],
};

function StatusPill({ status, testid }) {
  const [label, cls] = STATUS[status] || [status, ""];
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${cls}`} data-testid={testid}>{label}</span>;
}

function ExportPane({ rows }) {
  const [q, setQ] = useState("PhoneSuite Connectware");
  const [sel, setSel] = useState([]);
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [billing, setBilling] = useState(true);
  const [busy, setBusy] = useState(false);
  const shown = useMemo(() => rows.filter((r) => `${r.name} ${r.alias}`.toLowerCase().includes(q.trim().toLowerCase())), [rows, q]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const hasPhoneSuite = rows.some((r) => sel.includes(r.id) && (r.alias || "").startsWith("phonesuite_"));
  const ok = sel.length && pass.length >= 10 && pass === pass2;

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/api-connections/transfer/export",
        { ids: sel, passphrase: pass, include_billing_phonesuite: billing && hasPhoneSuite }, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "api-connections.json";
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${sel.length} connection${sel.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="conn-transfer-export">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter connections…" data-testid="conn-transfer-filter" />
        <Button variant="outline" onClick={() => setSel((s) => [...new Set([...s, ...shown.map((r) => r.id)])])}
                data-testid="conn-transfer-select-shown">Select {shown.length}</Button>
        <Button variant="ghost" onClick={() => setSel([])} data-testid="conn-transfer-clear">Clear</Button>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="conn-transfer-list">
        {shown.map((r) => (
          <label key={r.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
            <Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`conn-transfer-pick-${r.id}`} />
            <span className="flex-1 truncate text-slate-700">{r.name}</span>
            <span className="font-mono text-[11px] text-slate-400">{r.alias}</span>
          </label>
        ))}
        {!shown.length && <p className="px-3 py-4 text-center text-sm text-slate-400">No connections match.</p>}
      </div>
      {hasPhoneSuite && (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={billing} onCheckedChange={(v) => setBilling(!!v)} data-testid="conn-transfer-include-billing" />
          Include Billing Sync's PhoneSuite territory list for these connections
        </label>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Passphrase (10+ characters)</Label>
          <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)} data-testid="conn-transfer-pass" /></div>
        <div><Label>Repeat passphrase</Label>
          <Input type="password" value={pass2} onChange={(e) => setPass2(e.target.value)} data-testid="conn-transfer-pass2" /></div>
      </div>
      <p className="text-xs text-slate-500">The file holds the tokens, sealed with this passphrase — you'll need it again on import. Share them separately.</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500" data-testid="conn-transfer-selected-count">{sel.length} selected</span>
        <Button onClick={download} disabled={!ok || busy} className="gap-1.5" data-testid="conn-transfer-download">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download sealed file
        </Button>
      </div>
    </div>
  );
}

function ImportPane({ onDone }) {
  const [bundle, setBundle] = useState(null);
  const [fileName, setFileName] = useState("");
  const [pass, setPass] = useState("");
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    setPlan(null); setResult(null);
    if (!f) return;
    try { setBundle(JSON.parse(await f.text())); setFileName(f.name); }
    catch { setBundle(null); toast.error("That file isn't valid JSON"); }
  };
  const run = async (step) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api-connections/transfer/${step}`, { bundle, passphrase: pass });
      setPlan(data);
      if (step === "import") {
        setResult(data);
        toast.success(`Added ${data.added.length} connection${data.added.length === 1 ? "" : "s"}`);
        onDone();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Import failed");
    } finally {
      setBusy(false);
    }
  };
  const adds = plan ? plan.connections.filter((c) => c.status === "new").length + plan.billing.filter((b) => b.status === "new").length : 0;

  return (
    <div className="space-y-3" data-testid="conn-transfer-import">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Export file</Label>
          <Input type="file" accept=".json,application/json" onChange={pick} data-testid="conn-transfer-file" /></div>
        <div><Label>Passphrase</Label>
          <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)} data-testid="conn-transfer-import-pass" /></div>
      </div>
      {bundle && <p className="text-xs text-slate-500" data-testid="conn-transfer-file-summary">
        {fileName}: {bundle.connections} connection(s), {bundle.billing_territories} Billing Sync territories, from {bundle.source || "another environment"}
      </p>}
      <Button variant="outline" onClick={() => run("preview")} disabled={!bundle || !pass || busy} className="gap-1.5"
              data-testid="conn-transfer-check">
        {busy && !plan ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Unlock &amp; check
      </Button>
      {plan && (
        <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 text-sm" data-testid="conn-transfer-plan">
          {plan.connections.map((c, i) => (
            <div key={`c${i}`} className="flex items-center gap-3 border-b border-slate-100 px-3 py-2">
              <span className="flex-1 truncate text-slate-700">{c.name}</span>
              <StatusPill status={c.status} testid={`conn-transfer-plan-conn-${i}`} />
            </div>
          ))}
          {plan.billing.map((b, i) => (
            <div key={`b${i}`} className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 bg-slate-50/60">
              <span className="flex-1 truncate text-slate-600">Billing Sync territory · {b.territory}</span>
              <StatusPill status={b.status} testid={`conn-transfer-plan-billing-${i}`} />
            </div>
          ))}
        </div>
      )}
      {result ? (
        <p className="text-sm text-emerald-700" data-testid="conn-transfer-result">
          Added {result.added.length} connection(s) and {result.billing_added} Billing Sync territories; kept {result.skipped} that were already live.
          Press Test on the new ones to confirm their tokens.
        </p>
      ) : plan && (
        <Button onClick={() => run("import")} disabled={!adds || busy} className="gap-1.5" data-testid="conn-transfer-apply">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import {adds} new item{adds === 1 ? "" : "s"}
        </Button>
      )}
    </div>
  );
}

export function ApiConnectionsTransfer({ rows, onImported }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="gap-1.5 shrink-0" data-testid="api-conn-transfer-open">
        <ArrowRightLeft className="h-4 w-4" /> Move to another environment
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="api-conn-transfer-dialog">
          <DialogHeader>
            <DialogTitle>Move API connections</DialogTitle>
            <DialogDescription>
              Export a sealed file here, then import it in the other environment (e.g. production).
              Connections that already exist there are kept as they are.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="export">
            <TabsList>
              <TabsTrigger value="export" data-testid="conn-transfer-tab-export">Export</TabsTrigger>
              <TabsTrigger value="import" data-testid="conn-transfer-tab-import">Import</TabsTrigger>
            </TabsList>
            <TabsContent value="export"><ExportPane rows={rows} /></TabsContent>
            <TabsContent value="import"><ImportPane onDone={onImported} /></TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
